import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loading } from '../components/ui/SimpleLoading';
import { Alert } from '../components/ui/SimpleAlert';
import { apiService } from '../services/api.service';
import { useTenantStore } from '../stores/tenant.store';

type CsvReport = 'loans' | 'payments' | 'collections' | 'accounting-balance';

const CSV_EXPORTS: Array<{
  id: CsvReport;
  title: string;
  description: string;
  filename: string;
}> = [
  {
    id: 'loans',
    title: 'Préstamos',
    description: 'Listado completo de préstamos con cliente, producto y estado.',
    filename: 'loans.csv',
  },
  {
    id: 'payments',
    title: 'Pagos',
    description: 'Historial de pagos con desglose capital, interés y mora.',
    filename: 'payments.csv',
  },
  {
    id: 'collections',
    title: 'Cobranzas',
    description: 'Cartera de cobranzas pendientes y gestionadas.',
    filename: 'collections.csv',
  },
  {
    id: 'accounting-balance',
    title: 'Balance contable',
    description: 'Saldos por cuenta del plan de la empresa seleccionada (JournalEntry).',
    filename: 'accounting-balance.csv',
  },
];

export function Reports() {
  const legalEntityId = useTenantStore((s) => s.legalEntityId);
  const [stats, setStats] = useState<{
    totalCustomers: number;
    totalLoans: number;
    activeLoans: number;
    totalPayments: number;
    pendingCollections: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getReportsDashboardStats();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar resumen');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadCsv = async (report: CsvReport) => {
    setDownloading(report);
    setError(null);
    try {
      if (report === 'accounting-balance' && !legalEntityId) {
        throw new Error('Seleccioná una empresa en la barra superior para el balance contable');
      }
      await apiService.downloadReportCsv(
        report,
        report === 'accounting-balance' ? { legalEntityId: legalEntityId! } : undefined,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al descargar');
    } finally {
      setDownloading(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reportes</h1>
          <p className="text-sm text-gray-500 mt-1">
            Exportaciones instantáneas en CSV y reportes operativos en Excel.
          </p>
        </div>
        <Button onClick={loadStats}>Actualizar</Button>
      </div>

      {error && (
        <div className="mb-4">
          <Alert variant="error" title="Error">
            {error}
          </Alert>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Clientes', value: stats.totalCustomers },
            { label: 'Préstamos', value: stats.totalLoans },
            { label: 'Activos', value: stats.activeLoans },
            { label: 'Pagos', value: stats.totalPayments },
            { label: 'Cobranzas pend.', value: stats.pendingCollections },
          ].map((s) => (
            <Card key={s.label} className="text-center py-4">
              <p className="text-2xl font-semibold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500 mt-1">{s.label}</p>
            </Card>
          ))}
        </div>
      )}

      <h2 className="text-lg font-medium text-gray-900 mb-4">Exportar CSV</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {CSV_EXPORTS.map((item) => (
          <Card key={item.id}>
            <div className="flex justify-between items-start gap-4">
              <div>
                <h3 className="font-medium text-gray-900">{item.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                <p className="text-xs text-gray-400 mt-2">{item.filename}</p>
              </div>
              <Button
                size="sm"
                variant="primary"
                loading={downloading === item.id}
                onClick={() => downloadCsv(item.id)}
              >
                Descargar
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Card>
        <h2 className="text-lg font-medium text-gray-900 mb-2">Reportes operativos</h2>
        <p className="text-sm text-gray-500 mb-4">
          Morosidad, ventas por plan/usuario y financiado mensual con filtros por sucursal y fechas.
          Exportación a Excel (.xlsx).
        </p>
        <Link to="/reports/operational">
          <Button variant="secondary">Ir a reportes operativos</Button>
        </Link>
      </Card>
    </div>
  );
}
